import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import HeroSearchSection from '../../components/ui/HeroSearchSection';
import CategoryGrid from '../../components/ui/CategoryGrid';
import LocationBasedSection from '../../components/ui/LocationBasedSection';
import RecentListingsCarousel from '../../components/ui/RecentListingsCarousel';
import PremiumFeaturesSection from '../../components/ui/PremiumFeaturesSection';
import TrustSignalsSection from '../../components/ui/TrustSignalsSection';
import ServicesSection from '../../components/ui/ServicesSection';
import Icon from '../../components/AppIcon';

// Composant pour la popup de consentement des cookies
const CookieConsentPopup = ({ onAccept, onReject }) => {
  return (
    <div className="cookie-popup-overlay">
      <div className="cookie-popup">
        <div className="cookie-header">
          <Icon name="Cookie" className="cookie-icon" />
          <h3 className="cookie-title">Gestion des cookies</h3>
        </div>
        
        <div className="cookie-content">
          <p className="cookie-description">
            Nous utilisons des cookies pour améliorer votre expérience, 
            personnaliser le contenu et analyser le trafic. 
            Certains cookies sont nécessaires au fonctionnement du site.
          </p>
          
          <div className="cookie-details">
            <div className="cookie-detail-item">
              <Icon name="CheckCircle" className="detail-icon" />
              <span className="detail-text">Cookies essentiels</span>
            </div>
            <div className="cookie-detail-item">
              <Icon name="PieChart" className="detail-icon" />
              <span className="detail-text">Cookies d'analyse</span>
            </div>
            <div className="cookie-detail-item">
              <Icon name="Target" className="detail-icon" />
              <span className="detail-text">Cookies de personnalisation</span>
            </div>
          </div>
        </div>
        
        <div className="cookie-actions">
          <button 
            onClick={onReject}
            className="cookie-btn cookie-btn-reject"
          >
            Refuser les cookies optionnels
          </button>
          <button 
            onClick={onAccept}
            className="cookie-btn cookie-btn-accept"
          >
            Accepter tous les cookies
          </button>
        </div>
        
        <p className="cookie-policy">
          En continuant, vous acceptez notre{' '}
          <a href="/privacy" className="policy-link">
            Politique de confidentialité
          </a>
        </p>
      </div>
    </div>
  );
};

// Composant pour la popup d'installation de l'app mobile
const MobileAppPopup = ({ onInstall, onClose, userAgent }) => {
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  
  return (
    <div className="mobile-app-popup-overlay">
      <div className="mobile-app-popup">
        <div className="mobile-app-header">
          <Icon name="Smartphone" className="mobile-app-icon" />
          <h3 className="mobile-app-title">Installation de l'application</h3>
          <button onClick={onClose} className="mobile-app-close">
            <Icon name="X" className="close-icon" />
          </button>
        </div>
        
        <div className="mobile-app-content">
          <p className="mobile-app-description">
            Pour une meilleure expérience, nous vous recommandons d'utiliser notre application mobile.
          </p>
          
          <div className="mobile-app-benefits">
            <div className="benefit-item">
              <Icon name="Zap" className="benefit-icon" />
              <span className="benefit-text">Navigation plus rapide</span>
            </div>
            <div className="benefit-item">
              <Icon name="Bell" className="benefit-icon" />
              <span className="benefit-text">Notifications en temps réel</span>
            </div>
            <div className="benefit-item">
              <Icon name="Shield" className="benefit-icon" />
              <span className="benefit-text">Sécurité renforcée</span>
            </div>
            <div className="benefit-item">
              <Icon name="Download" className="benefit-icon" />
              <span className="benefit-text">Accès hors ligne</span>
            </div>
          </div>
        </div>
        
        <div className="mobile-app-actions">
          {isIOS ? (
            <button 
              onClick={() => onInstall('ios')}
              className="mobile-app-btn ios-btn"
            >
              <Icon name="Apple" className="store-icon" />
              <span className="btn-text">Installer sur l'App Store</span>
            </button>
          ) : isAndroid ? (
            <button 
              onClick={() => onInstall('android')}
              className="mobile-app-btn android-btn"
            >
              <Icon name="PlayStore" className="store-icon" />
              <span className="btn-text">Installer sur Google Play</span>
            </button>
          ) : (
            <div className="store-buttons">
              <button 
                onClick={() => onInstall('ios')}
                className="mobile-app-btn ios-btn"
              >
                <Icon name="Apple" className="store-icon" />
                <span className="btn-text">App Store</span>
              </button>
              <button 
                onClick={() => onInstall('android')}
                className="mobile-app-btn android-btn"
              >
                <Icon name="PlayStore" className="store-icon" />
                <span className="btn-text">Google Play</span>
              </button>
            </div>
          )}
          
          <button 
            onClick={onClose}
            className="mobile-app-btn later-btn"
          >
            <span className="btn-text">Continuer sur le site web</span>
          </button>
        </div>
        
        <p className="mobile-app-note">
          L'application est disponible gratuitement sur les stores
        </p>
      </div>
    </div>
  );
};

const HomepageDashboard = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const robotRef = useRef(null);
  const [robotScale, setRobotScale] = useState(1);
  const [robotShadow, setRobotShadow] = useState('0 0 0 rgba(246, 138, 59, 0)');
  const [robotY, setRobotY] = useState(30);
  const [robotOpacity, setRobotOpacity] = useState(0);
  const [showCookiePopup, setShowCookiePopup] = useState(false);
  const [showMobileAppPopup, setShowMobileAppPopup] = useState(false);
  const [userAgent, setUserAgent] = useState('');

  // Fonction pour détecter si on est sur mobile
  const isMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
  };

  // Fonction pour vérifier si l'app est déjà installée (via PWA)
  const isAppInstalled = () => {
    // Vérifier si l'application est en mode standalone (installée)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    
    // Pour iOS
    if (window.navigator.standalone === true) {
      return true;
    }
    
    // Pour Android Chrome
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    
    // Vérifier dans localStorage si l'utilisateur a déjà choisi de rester sur le web
    const mobileRedirectChoice = localStorage.getItem('mobileRedirectChoice');
    if (mobileRedirectChoice === 'stayOnWeb') {
      return true; // Considérer comme "installée" pour éviter de redemander
    }
    
    return false;
  };

  // Fonction pour ouvrir l'application mobile
  const openMobileApp = () => {
    // URL de l'application (à remplacer par vos URLs réelles)
    const appStoreUrl = 'https://apps.apple.com/fr/app/autocasse/com.autocasses';
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.autocasses.app';
    
    // Intent URL pour Android (pour ouvrir directement l'app si installée)
    const androidIntentUrl = 'intent://autocasse.cm/#Intent;scheme=https;package=com.autocasse.app;S.browser_fallback_url=https://play.google.com/store/apps/details?id=com.autocasse.app;end';
    
    // Universal Link pour iOS
    const iosUniversalLink = 'https://autocasse.cm/app';
    
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    
    if (isIOS) {
      // Essayer d'ouvrir l'Universal Link (ouvre l'app si installée)
      window.location.href = iosUniversalLink;
      
      // Fallback vers l'App Store après un délai
      setTimeout(() => {
        if (document.hidden) {
          return; // L'app s'est ouverte
        }
        window.location.href = appStoreUrl;
      }, 2500);
    } else if (isAndroid) {
      // Essayer d'ouvrir via Intent
      window.location.href = androidIntentUrl;
    } else {
      // Pour les autres devices, rediriger vers le store approprié
      window.open(isIOS ? appStoreUrl : playStoreUrl, '_blank');
    }
  };

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà donné son consentement
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Afficher la popup après un délai pour une meilleure expérience utilisateur
      const timer = setTimeout(() => {
        setShowCookiePopup(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    // Détection mobile et redirection vers l'app
    const checkMobileAndRedirect = () => {
      const userAgentString = navigator.userAgent || navigator.vendor || window.opera;
      setUserAgent(userAgentString);
      
      // Vérifier si on est sur mobile et que l'app n'est pas déjà installée
      if (isMobileDevice() && !isAppInstalled()) {
        // Vérifier si l'utilisateur a déjà vu la popup aujourd'hui
        const lastPopupShown = localStorage.getItem('lastMobilePopupShown');
        const today = new Date().toISOString().split('T')[0];
        
        if (lastPopupShown !== today) {
          // Afficher la popup après 2 secondes
          setTimeout(() => {
            setShowMobileAppPopup(true);
            localStorage.setItem('lastMobilePopupShown', today);
          }, 2000);
        }
      }
    };

    checkMobileAndRedirect();

    // SEO et titre de la page
    document.title = 'AutoCasse - Marketplace de Pièces Auto';

    // Animation d'entrée du robot
    const entranceAnimation = async () => {
      // Phase 1: Apparition et rebond
      setRobotOpacity(0);
      setRobotY(30);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      setRobotOpacity(1);
      setRobotY(0);
      
      // Animation CSS pour le rebond
      robotRef.current?.classList.add('animate-bounce-in');
      
      // Phase 2: Pulse après 1 seconde
      setTimeout(() => {
        let pulseCount = 0;
        const pulseInterval = setInterval(() => {
          setRobotScale(prev => prev === 1 ? 1.1 : 1);
          setRobotShadow(prev => 
            prev === '0 0 0 rgba(246, 138, 59, 0.5)' 
              ? '0 0 10px rgba(246, 138, 59, 0.8)' 
              : '0 0 0 rgba(246, 138, 59, 0.5)'
          );
          pulseCount++;
          
          if (pulseCount >= 6) { // 3 pulses complets (2 changements par pulse)
            clearInterval(pulseInterval);
            setRobotScale(1);
            setRobotShadow('0 0 5px rgba(246, 138, 59, 0.5)');
          }
        }, 600);
      }, 1000);
    };

    entranceAnimation();

    // Nettoyage
    return () => {
      if (robotRef.current) {
        robotRef.current.classList.remove('animate-bounce-in');
      }
    };
  }, []);

  const openWhatsApp = () => {
    const phoneNumber = '+2376XXXXXXXXX';
    const url = `https://wa.me/${phoneNumber}`;
    window.open(url, '_blank');
  };

  const openEmail = () => {
    const email = 'support@autocasse.cm';
    const url = `mailto:${email}`;
    window.location.href = url;
  };

  const handleRobotClick = () => {
    // Animation au clic
    setRobotScale(0.9);
    setTimeout(() => {
      setRobotScale(1);
      navigate('/bot');
    }, 150);
  };

  const handleAcceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowCookiePopup(false);
    
    // Ici vous pouvez initialiser vos services de tracking (Google Analytics, etc.)
    console.log('Cookies acceptés - Initialisation des services de tracking');
  };

  const handleRejectCookies = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowCookiePopup(false);
    
    // Ici vous pouvez désactiver les cookies non essentiels
    console.log('Cookies refusés - Désactivation des services de tracking');
  };

  const handleInstallApp = (platform) => {
    // Sauvegarder le choix de l'utilisateur
    localStorage.setItem('mobileRedirectChoice', 'redirectToApp');
    
    if (platform === 'ios') {
      window.open('https://apps.apple.com/fr/app/autocasse/idVOTRE_ID', '_blank');
    } else if (platform === 'android') {
      window.open('https://play.google.com/store/apps/details?id=com.autocasse.app', '_blank');
    }
    
    setShowMobileAppPopup(false);
  };

  const handleStayOnWeb = () => {
    // Sauvegarder le choix de l'utilisateur
    localStorage.setItem('mobileRedirectChoice', 'stayOnWeb');
    setShowMobileAppPopup(false);
  };

  return (
    <div className="homepage-layout">
      <Helmet>
        <title>AutoCasse - Marketplace de Pièces Auto</title>
        <meta 
          name="description" 
          content="Trouvez et vendez des pièces automobiles neuves et d'occasion. Marketplace sécurisé pour auto, moto, moteurs et accessoires." 
        />
        <meta name="keywords" content="pièces auto, marketplace, automobile, moto, moteurs, accessoires" />
        <meta property="og:title" content="AutoCasse - Marketplace de Pièces Auto" />
        <meta property="og:description" content="Trouvez et vendez des pièces automobiles neuves et d'occasion" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/" />
        
        {/* Meta tags pour PWA et app mobile */}
        <meta name="apple-itunes-app" content="app-id=VOTRE_ID_APP_STORE" />
        <meta name="google-play-app" content="app-id=com.autocasse.app" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
      </Helmet>

      {/* Popup de consentement des cookies */}
      {showCookiePopup && (
        <CookieConsentPopup 
          onAccept={handleAcceptCookies}
          onReject={handleRejectCookies}
        />
      )}

      {/* Popup d'installation de l'app mobile */}
      {showMobileAppPopup && (
        <MobileAppPopup 
          onInstall={handleInstallApp}
          onClose={handleStayOnWeb}
          userAgent={userAgent}
        />
      )}

      {/* Bouton Robot avec animation */}
      <div 
        ref={robotRef}
        className="robot-button"
        style={{
          transform: `translateY(${robotY}px) scale(${robotScale})`,
          opacity: robotOpacity,
          boxShadow: robotShadow,
        }}
      >
        <button
          onClick={handleRobotClick}
          className="robot-button-inner"
          aria-label="Ouvrir l'assistant robot"
        >
          <Icon name="Bot" className="robot-icon" />
        </button>
      </div>

      {/* Bouton Notification */}
      <div className="notification-button">
        <button
          onClick={() => navigate('/notifications')}
          className="notification-button-inner"
          aria-label="Notifications"
        >
          <div className="notification-badge">
            <span className="badge-text"></span>
          </div>
          <Icon name="Bell" className="notification-icon" />
        </button>
      </div>
      
      {/* Header */}
      <Header />
      
      <main className="main-content">
        {/* Hero Search Section */}
        <HeroSearchSection />
        
        {/* Category Grid */}
        <CategoryGrid />
        
        {/* Location Based Section (commenté) */}
        {/* <LocationBasedSection /> */}
        
        {/* Recent Listings Carousel */}
        <RecentListingsCarousel />
        
        {/* Nouvelle Section: Liste des Services */}
        <ServicesSection />
        
        {/* Premium Features Section (commenté) */}
        {/* <PremiumFeaturesSection /> */}
        
        {/* Trust Signals Section */}
        {/* <TrustSignalsSection /> */}
      </main>

      {/* Footer compact sur 1 ligne */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main-row">
            {/* Logo et copyright */}
            <div className="footer-left">
              <div className="logo-copyright-container">
                <img 
                  src="/assets/images/AutoCass1.png" 
                  alt="Logo AutoCasse" 
                  className="footer-logo" 
                />
                <p className="copyright-text">
                  © {currentYear} AutoCasse. Tous droits réservés.
                </p>
              </div>
              
              {/* Statut service */}
              <div className="status-container">
                <div className="status-indicator">
                  <div className="status-dot"></div>
                  <span className="status-text">Service Actif 24/7</span>
                </div>
              </div>
            </div>

            {/* Liens légaux */}
            <div className="footer-right">
              <div className="legal-links">
                <a 
                  href="/terms" 
                  className="legal-link"
                >
                  Conditions d'utilisation
                </a>
                <a 
                  href="/privacy" 
                  className="legal-link"
                >
                  Politique de confidentialité
                </a>
              </div>
              
              {/* Support contact */}
              <div className="support-contact">
                <button
                  onClick={openWhatsApp}
                  className="support-button"
                >
                  <Icon name="MessageCircle" className="support-icon" />
                  <span className="support-text">WhatsApp</span>
                </button>
                <button
                  onClick={openEmail}
                  className="support-button"
                >
                  <Icon name="Mail" className="support-icon" />
                  <span className="support-text">Email</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        /* Styles généraux */
        .homepage-layout {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background-color: #ffffff;
          position: relative;
        }

        .main-content {
          flex: 1;
          padding-bottom: 2rem;
        }

        /* Popup de consentement des cookies */
        .cookie-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .cookie-popup {
          background-color: white;
          border-radius: 12px;
          padding: 1.5rem;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: cookieSlideUp 0.4s ease-out;
        }

        .cookie-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .cookie-icon {
          color: #f68a3b;
          font-size: 1.5rem;
        }

        .cookie-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }

        .cookie-content {
          margin-bottom: 1.5rem;
        }

        .cookie-description {
          color: #6b7280;
          font-size: 0.9375rem;
          line-height: 1.5;
          margin-bottom: 1rem;
        }

        .cookie-details {
          background-color: #f9fafb;
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .cookie-detail-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .cookie-detail-item:last-child {
          margin-bottom: 0;
        }

        .detail-icon {
          color: #f68a3b;
          font-size: 1rem;
        }

        .detail-text {
          font-size: 0.875rem;
          color: #374151;
        }

        .cookie-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        @media (min-width: 480px) {
          .cookie-actions {
            flex-direction: row;
            justify-content: space-between;
          }
        }

        .cookie-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          flex: 1;
        }

        .cookie-btn-reject {
          background-color: white;
          color: #6b7280;
          border: 1px solid #d1d5db;
        }

        .cookie-btn-reject:hover {
          background-color: #f9fafb;
          border-color: #9ca3af;
        }

        .cookie-btn-accept {
          background-color: #f68a3b;
          color: white;
        }

        .cookie-btn-accept:hover {
          background-color: #e5792a;
        }

        .cookie-policy {
          font-size: 0.75rem;
          color: #6b7280;
          text-align: center;
          margin: 0;
        }

        .policy-link {
          color: #f68a3b;
          text-decoration: none;
        }

        .policy-link:hover {
          text-decoration: underline;
        }

        /* Popup d'installation de l'app mobile */
        .mobile-app-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          padding: 1rem;
          backdrop-filter: blur(5px);
        }

        .mobile-app-popup {
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 16px;
          padding: 1.5rem;
          max-width: 450px;
          width: 100%;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          animation: mobileAppSlideUp 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          border: 1px solid #e2e8f0;
          position: relative;
        }

        .mobile-app-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .mobile-app-icon {
          color: #f68a3b;
          font-size: 2rem;
        }

        .mobile-app-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
          flex: 1;
          text-align: center;
        }

        .mobile-app-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mobile-app-close:hover {
          background-color: #f1f5f9;
        }

        .close-icon {
          color: #64748b;
          font-size: 1.25rem;
        }

        .mobile-app-content {
          margin-bottom: 1.5rem;
        }

        .mobile-app-description {
          color: #475569;
          font-size: 0.9375rem;
          line-height: 1.5;
          margin-bottom: 1rem;
          text-align: center;
        }

        .mobile-app-benefits {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
          margin-top: 1.5rem;
        }

        .benefit-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background-color: #f8fafc;
          padding: 0.75rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .benefit-icon {
          color: #f68a3b;
          font-size: 1rem;
        }

        .benefit-text {
          font-size: 0.8125rem;
          color: #334155;
          font-weight: 500;
        }

        .mobile-app-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .store-buttons {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        .mobile-app-btn {
          padding: 1rem 1.5rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 52px;
        }

        .ios-btn {
          background: linear-gradient(135deg, #000000 0%, #333333 100%);
          color: white;
        }

        .ios-btn:hover {
          background: linear-gradient(135deg, #333333 0%, #000000 100%);
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
        }

        .android-btn {
          background: linear-gradient(135deg, #3ddc84 0%, #0f9d58 100%);
          color: white;
        }

        .android-btn:hover {
          background: linear-gradient(135deg, #0f9d58 0%, #3ddc84 100%);
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(15, 157, 88, 0.3);
        }

        .later-btn {
          background-color: white;
          color: #64748b;
          border: 2px solid #e2e8f0;
        }

        .later-btn:hover {
          background-color: #f8fafc;
          border-color: #cbd5e1;
          transform: translateY(-2px);
        }

        .store-icon {
          font-size: 1.25rem;
        }

        .btn-text {
          font-size: 0.875rem;
          font-weight: 600;
        }

        .mobile-app-note {
          font-size: 0.75rem;
          color: #94a3b8;
          text-align: center;
          margin: 1rem 0 0 0;
          font-style: italic;
        }

        /* Styles des boutons flottants */
        .robot-button {
          position: fixed;
          bottom: 96px;
          right: 16px;
          z-index: 50;
          transition: all 0.3s ease-out;
        }

        .robot-button-inner {
          background-color: #f68a3b;
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 4px solid white;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .robot-button-inner:hover {
          background-color: #e5792a;
        }

        .robot-button-inner:active {
          transform: scale(0.95);
        }

        .robot-icon {
          color: white;
          font-size: 1.875rem;
        }

        .notification-button {
          position: fixed;
          bottom: 32px;
          right: 16px;
          z-index: 50;
        }

        .notification-button-inner {
          background-color: #3b82f6;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          cursor: pointer;
          transition: background-color 0.2s;
          border: none;
        }

        .notification-button-inner:hover {
          background-color: #2563eb;
        }

        .notification-button-inner:active {
          transform: scale(0.95);
        }

        .notification-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          background-color: #f68a3b;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .badge-text {
          font-size: 0.75rem;
          color: white;
          font-weight: bold;
        }

        .notification-icon {
          color: white;
          font-size: 1.5rem;
        }

        /* Footer compact sur 1 ligne */
        .footer {
          background-color: #ffffff;
          border-top: 1px solid #e5e7eb;
          padding: 0.75rem 1rem;
          margin-top: auto;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
        }

        .footer-main-row {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        @media (min-width: 768px) {
          .footer-main-row {
            flex-direction: row;
            align-items: center;
            gap: 2rem;
          }
        }

        /* Section gauche du footer */
        .footer-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
        }

        @media (min-width: 768px) {
          .footer-left {
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            gap: 2rem;
            width: auto;
          }
        }

        .logo-copyright-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        @media (min-width: 768px) {
          .logo-copyright-container {
            flex-direction: row;
            align-items: center;
            gap: 1.5rem;
          }
        }

        .footer-logo {
          width: 140px;
          height: auto;
          object-fit: contain;
        }

        .copyright-text {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0;
          text-align: center;
        }

        @media (min-width: 768px) {
          .copyright-text {
            text-align: left;
          }
        }

        .status-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (min-width: 768px) {
          .status-container {
            justify-content: flex-start;
          }
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background-color: #f0fdf4;
          padding: 0.375rem 0.75rem;
          border-radius: 9999px;
          border: 1px solid #dcfce7;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          background-color: #10b981;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .status-text {
          font-size: 0.875rem;
          color: #059669;
          font-weight: 500;
          white-space: nowrap;
        }

        /* Section droite du footer */
        .footer-right {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          width: 100%;
        }

        @media (min-width: 768px) {
          .footer-right {
            flex-direction: column;
            align-items: flex-end;
            gap: 0.75rem;
            width: auto;
          }
        }

        .legal-links {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        @media (min-width: 768px) {
          .legal-links {
            justify-content: flex-end;
          }
        }

        .legal-link {
          color: #6b7280;
          font-size: 0.875rem;
          text-decoration: none;
          transition: color 0.2s;
          white-space: nowrap;
        }

        .legal-link:hover {
          color: #f68a3b;
        }

        .support-contact {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        @media (min-width: 768px) {
          .support-contact {
            justify-content: flex-end;
          }
        }

        .support-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: none;
          border: none;
          padding: 0.375rem 0.75rem;
          border-radius: 0.375rem;
          cursor: pointer;
          transition: all 0.2s;
          color: #4b5563;
        }

        .support-button:hover {
          background-color: #f9fafb;
        }

        .support-button:first-child:hover {
          color: #25D366;
        }

        .support-button:last-child:hover {
          color: #f68a3b;
        }

        .support-icon {
          font-size: 1rem;
        }

        .support-text {
          font-size: 0.875rem;
          font-weight: 500;
          white-space: nowrap;
        }

        /* Animations */
        @keyframes cookieSlideUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes mobileAppSlideUp {
          from {
            opacity: 0;
            transform: translateY(60px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .animate-bounce-in {
          animation: bounceIn 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        /* Responsive pour très petits écrans */
        @media (max-width: 480px) {
          .footer {
            padding: 0.5rem;
          }
          
          .footer-logo {
            width: 120px;
          }
          
          .legal-links {
            gap: 1rem;
          }
          
          .support-contact {
            gap: 0.75rem;
          }
          
          .support-button {
            padding: 0.25rem 0.5rem;
          }
          
          .status-indicator {
            padding: 0.25rem 0.5rem;
          }
          
          .mobile-app-popup {
            padding: 1rem;
          }
          
          .mobile-app-benefits {
            grid-template-columns: 1fr;
          }
          
          .store-buttons {
            grid-template-columns: 1fr;
          }
        }

        /* Responsive pour écrans moyens */
        @media (min-width: 640px) and (max-width: 767px) {
          .footer-main-row {
            flex-direction: row;
            flex-wrap: wrap;
          }
          
          .footer-left, .footer-right {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default HomepageDashboard;